import { useState, useEffect, useRef } from "react";
import {
  ChevronLeft, RefreshCw, Edit3, Calendar, Clock, MessageCircle, Phone,
  CheckCircle2, Plus, Trash2,
} from "lucide-react";
import { Card } from "../../components/shared/Card";
import { Btn } from "../../components/shared/Btn";
import { Badge } from "../../components/shared/Badge";
import { StatusBadge } from "../../components/shared/StatusBadge";
import { PhoneTap } from "../../components/shared/PhoneTap";
import { WhatsAppTap } from "../../components/shared/WhatsAppTap";
import { toast } from "../../components/shared/Toast";
import { useAppData } from "../../lib/AppDataContext";
import { leadsApi } from "../../lib/leadsApi";
import {
  STATUSES, STATUS_META, TEMPERATURE_META, configLabel, isOverdue,
  getAssignees, isAssignedTo, allowsFollowUp, fmtDate, fmtTime,
} from "../../lib/leads";
import { LeadForm } from "./LeadForm";

/* Lead detail screen — port of OLD admin 2830-3116. Three areas:

   1. Header card: avatar + name + project, status / temperature / overdue
      badges, Transfer popover, Edit button. Contact + key fields grid.
      Call / WhatsApp / Schedule follow-up CTA row.
   2. Activity timeline: chronological events with type-keyed icons,
      plus an "Add a note" composer beneath.
   3. Right rail: Status dropdown, Assigned-to summary (hidden for sales),
      Temperature chip set. */
export function LeadDetail({ lead, role, onBack, onUpdate, statusVersion }) {
  const { allSalesPeople, deleteLead, addLeadNote } = useAppData();
  // The list passes a row lead (no statusHistory/notes). Fetch the full
  // record on open so the activity timeline shows real status changes + notes.
  const [fullLead, setFullLead] = useState(lead);
  useEffect(() => {
    let alive = true;
    setFullLead(lead); // seed with what we have so the page renders immediately
    if (lead?.id) {
      leadsApi.getOne(lead.id)
        .then((full) => { if (alive && full) setFullLead((prev) => ({ ...prev, ...full })); })
        .catch(() => { /* keep row lead; timeline falls back to created-only */ });
    }
    return () => { alive = false; };
  }, [lead?.id]);
  const [status, setStatus] = useState(lead.status);
  const [note, setNote] = useState("");
  const [notePosting, setNotePosting] = useState(false);

  // The "Add note" button had no onClick at all — the state, the context
  // method and the API client all existed, but nothing connected them, so
  // typing a note and clicking did nothing.
  //
  // Re-fetches after posting rather than appending optimistically: the
  // timeline reads fullLead.notes, and the server owns the note's id,
  // timestamp and author. Mirrors the mount effect above.
  const handleAddNote = async () => {
    const body = note.trim();
    if (!body || notePosting) return;
    setNotePosting(true);
    try {
      await addLeadNote(lead.id, body);
      setNote("");
      const full = await leadsApi.getOne(lead.id);
      if (full) setFullLead((prev) => ({ ...prev, ...full }));
      toast("Note added");
    } catch (e) {
      toast(e?.message || "Could not add note", "error");
    } finally {
      setNotePosting(false);
    }
  };
  const [editing, setEditing] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [transferOpen, setTransferOpen] = useState(false);

  // Soft delete — the API sets leads.deleted_at, so this is reversible by
  // super_admin. deleteLead() re-throws on failure AND optimistically drops
  // the row from context state, so on success we only need to navigate back.
  const canDelete = role === "admin" || role === "super_admin";
  const handleDelete = async () => {
    if (!window.confirm(
      `Delete lead "${lead.name}"?\n\nThis is reversible by super_admin.`
    )) return;
    setDeleting(true);
    try {
      await deleteLead(lead.id);
      toast("Lead deleted");
      onBack();
    } catch (e) {
      toast(e?.message || "Delete failed", "error");
      setDeleting(false);
    }
  };
  const transferRef = useRef(null);

  useEffect(() => {
    if (!transferOpen) return;
    const onDoc = (e) => {
      if (transferRef.current && !transferRef.current.contains(e.target)) setTransferOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [transferOpen]);

  const transfer = (toId) => {
    setTransferOpen(false);
    const currentIds = getAssignees(lead);
    const nextAssign = toId ? [toId] : [];
    if (currentIds.length === nextAssign.length && currentIds.every((id, i) => id === nextAssign[i])) return;
    const updated = { ...lead, assignedTo: nextAssign, lastActivity: new Date() };
    onUpdate?.(updated);
    const targetName = toId ? allSalesPeople.find((s) => (s.userId || s.id) === toId)?.name : "Unassigned";
    toast(`Lead transferred to ${targetName}`);
  };

  const assigneeIds = getAssignees(lead);
  const sales = assigneeIds.length > 0 ? allSalesPeople.find((s) => (s.userId || s.id) === assigneeIds[0]) : null;
  const overdue = isOverdue(lead);

  // Real activity timeline — built from data the API actually returns:
  // lead creation, real status-history transitions, and real notes.
  // (GET /leads/:id includes statusHistory[] and notes[]; toUiLead carries them.)
  const timeline = (() => {
    const ev = [];
    const created = fullLead.createdAt || lead.createdAt;
    if (created) {
      ev.push({ type: "created", text: `Lead captured via ${fullLead.source || lead.source || "—"}`, when: new Date(created), who: fullLead.createdBy?.name || "System" });
    }
    for (const h of (fullLead.statusHistory || [])) {
      const when = h.createdAt || h.changedAt;
      const toStatus = h.toStatus || h.status;
      if (!when || !toStatus) continue;
      const label = STATUS_META[toStatus]?.label || toStatus;
      const fromLabel = h.fromStatus ? (STATUS_META[h.fromStatus]?.label || h.fromStatus) : null;
      ev.push({
        type: "status",
        text: fromLabel ? `Status: ${fromLabel} → ${label}` : `Status changed to ${label}`,
        when: new Date(when),
        who: h.changedBy?.name || "System",
      });
    }
    for (const n of (fullLead.notes || [])) {
      const when = n.createdAt || n.at;
      const text = n.body || n.text;
      if (!when || !text) continue;
      ev.push({
        type: "note",
        text,
        when: new Date(when),
        who: n.author?.name || "Team",
      });
    }
    // Oldest first in the timeline (reversed direction)
    return ev.sort((a, b) => a.when - b.when);
  })();

  return (
    <div>
      <button onClick={onBack} className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-900 mb-4">
        <ChevronLeft className="w-4 h-4" />Back to leads
      </button>
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          <Card className={`p-5 ${overdue ? "ring-2 ring-red-300 bg-red-50/30" : ""}`}>
            <div className="flex items-start justify-between">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-full bg-slate-100 grid place-items-center text-base font-semibold text-slate-600">
                  {lead.name.split(" ").map((p) => p[0]).join("")}
                </div>
                <div>
                  <h2 className="text-lg font-semibold text-slate-900">{lead.name}</h2>
                  <p className="text-sm text-slate-500">{lead.project || "—"}{lead.propertyType ? " · " + ({ apartment: "Apartment", villa: "Villa", plot: "Plot", duplex: "Duplex", studio: "Studio Apartment", commercial: "Commercial" }[lead.propertyType] || lead.propertyType) : ""}{configLabel(lead.config) ? " · " + configLabel(lead.config) : ""}</p>
                </div>
              </div>
              {/* flex-wrap is load-bearing: this row holds up to six children
                  (overdue badge, temperature, status, Transfer, Edit, Delete)
                  and without wrapping it overflows the card on a 1280-1440px
                  laptop once the ~260px sidebar is accounted for. justify-end
                  keeps wrapped items aligned with the buttons above them. */}
              <div className="flex flex-wrap items-center justify-end gap-2 min-w-0">
                {overdue && <Badge cls="bg-red-50 text-red-700 ring-red-600/20">Overdue</Badge>}
                {(() => {
                  const t = TEMPERATURE_META[lead.temperature] || TEMPERATURE_META.warm;
                  return <Badge cls={t.cls}>{t.label}</Badge>;
                })()}
                <StatusBadge status={status} />
                <div className="relative" ref={transferRef}>
                  <button
                    onClick={() => setTransferOpen((v) => !v)}
                    className="inline-flex items-center gap-1.5 text-xs font-medium px-2.5 py-1.5 rounded-lg ring-1 ring-inset ring-slate-300 bg-white text-slate-700 hover:bg-slate-50"
                    title={assigneeIds.length > 0
                      ? `Currently assigned to ${assigneeIds.map((id) => allSalesPeople.find((s) => (s.userId || s.id) === id)?.name || id).join(", ")}`
                      : "Lead is unassigned"}
                  >
                    <RefreshCw className="w-3.5 h-3.5" />
                    Transfer
                  </button>
                  {transferOpen && (
                    <div className="absolute right-0 top-full mt-1 z-30 w-56 bg-white rounded-lg ring-1 ring-slate-200 shadow-lg overflow-hidden">
                      <div className="px-3 py-2 border-b border-slate-100 bg-slate-50">
                        <div className="text-[11px] font-semibold text-slate-500 uppercase tracking-wide">Reassign to</div>
                        <div className="text-[10px] text-slate-400 mt-0.5">Replaces all current assignments &middot; use edit form for multi-assign</div>
                      </div>
                      <div className="max-h-64 overflow-y-auto py-1">
                        <button
                          onClick={() => transfer(null)}
                          className={`w-full text-left px-3 py-2 text-sm flex items-center justify-between hover:bg-slate-50 ${assigneeIds.length === 0 ? "text-slate-400" : "text-slate-700"}`}
                          disabled={assigneeIds.length === 0}
                        >
                          <span>Unassign</span>
                          {assigneeIds.length === 0 && <CheckCircle2 className="w-3.5 h-3.5" />}
                        </button>
                        {allSalesPeople.filter((s) => s.active !== false && s.userActive !== false && s.role === "employee").map((s) => {
                          const sid = s.userId || s.id;
                          const isCurrent = assigneeIds.length === 1 && assigneeIds[0] === sid;
                          const isAmong = isAssignedTo(lead, sid);
                          return (
                            <button
                              key={s.id}
                              onClick={() => transfer(sid)}
                              disabled={isCurrent}
                              className={`w-full text-left px-3 py-2 text-sm flex items-center justify-between hover:bg-slate-50 ${isCurrent ? "bg-blue-50/50 text-blue-700 font-medium" : isAmong ? "text-blue-700" : "text-slate-700"}`}
                            >
                              <span>{s.name}{isAmong && !isCurrent ? <span className="text-[10px] text-slate-400 ml-1">(co-assigned)</span> : null}</span>
                              {isCurrent && <CheckCircle2 className="w-3.5 h-3.5 text-blue-600" />}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </div>
                <Btn variant="secondary" size="sm" onClick={() => setEditing(true)}><Edit3 className="w-3.5 h-3.5" />Edit</Btn>
                {canDelete && (
                  <button
                    type="button"
                    onClick={handleDelete}
                    disabled={deleting}
                    className="shrink-0 whitespace-nowrap inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg ring-1 ring-inset ring-red-300 bg-white text-red-600 text-xs font-medium hover:bg-red-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                    {deleting ? "Deleting…" : "Delete"}
                  </button>
                )}
              </div>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 mt-5 pt-5 border-t border-slate-100">
              <div>
                <div className="text-xs text-slate-400 mb-1">Phone</div>
                <div className="text-sm font-medium text-slate-900 tabular-nums">
                  <PhoneTap mobile={lead.mobile} whatsapp={lead.whatsapp} display={lead.phone} />
                  {lead.whatsappOnly && (
                    <span className="ml-2 text-[10px] font-semibold rounded-pill bg-green-50 text-green-700 ring-1 ring-inset ring-green-600/20 px-2 py-0.5">
                      WhatsApp only
                    </span>
                  )}
                </div>
              </div>
              <div>
                <div className="text-xs text-slate-400 mb-1">WhatsApp</div>
                <div className="text-sm">
                  <WhatsAppTap mobile={lead.whatsapp || lead.mobile} label={"+91 " + (lead.whatsapp || lead.mobile)} />
                </div>
              </div>
              <div><div className="text-xs text-slate-400 mb-1">Email</div><div className="text-sm font-medium text-slate-900 truncate">{lead.email || "—"}</div></div>
              <div><div className="text-xs text-slate-400 mb-1">Occupation</div><div className="text-sm font-medium text-slate-900">{lead.occupation === "Other" && lead.occupationOther ? `Other: ${lead.occupationOther}` : (lead.occupation || "—")}</div></div>
              <div><div className="text-xs text-slate-400 mb-1">Company</div><div className="text-sm font-medium text-slate-900">{lead.companyName || "—"}</div></div>
              <div><div className="text-xs text-slate-400 mb-1">Source</div><div className="text-sm font-medium text-slate-900">{lead.source === "Other" && lead.sourceOther ? `Other: ${lead.sourceOther}` : lead.source}</div></div>
              <div><div className="text-xs text-slate-400 mb-1">Budget</div><div className="text-sm font-medium text-slate-900">{lead.budget || "—"}</div></div>
              <div><div className="text-xs text-slate-400 mb-1">Buying Purpose</div><div className="text-sm font-medium text-slate-900">{lead.buyingPurpose || "—"}</div></div>
              <div>
                <div className="text-xs text-slate-400 mb-1">Assigned to</div>
                <div className="text-sm font-medium text-slate-900">
                  {sales ? <Badge cls="bg-blue-50 text-blue-700 ring-blue-600/20">{sales.name}</Badge> : <Badge cls="bg-amber-50 text-amber-700 ring-amber-600/20">Unassigned</Badge>}
                </div>
              </div>
              <div><div className="text-xs text-slate-400 mb-1">Reference</div><div className="text-sm font-medium text-slate-900">{lead.reference || "—"}</div></div>
              <div><div className="text-xs text-slate-400 mb-1">Address</div><div className="text-sm font-medium text-slate-900 truncate">{lead.address || "—"}</div></div>
              <div>
                <div className="text-xs text-slate-400 mb-1">Property Type</div>
                <div className="text-sm font-medium text-slate-900">
                  {lead.propertyType
                    ? ({ apartment: "Apartment", villa: "Villa", plot: "Plot", duplex: "Duplex", studio: "Studio Apartment", commercial: "Commercial" }[lead.propertyType] || lead.propertyType)
                    : "—"}
                </div>
              </div>
              <div>
                <div className="text-xs text-slate-400 mb-1">Config</div>
                <div className="text-sm font-medium text-slate-900">
                  {(() => {
                    const cfgs = Array.isArray(lead.config) ? lead.config : (lead.config ? [lead.config] : []);
                    return cfgs.length ? cfgs.map((c) => <Badge key={c} cls="bg-blue-50 text-blue-700 ring-blue-600/20 mr-1 mb-1">{c}</Badge>) : "—";
                  })()}
                </div>
              </div>
              <div>
                <div className="text-xs text-slate-400 mb-1">Possession</div>
                <div className="text-sm font-medium text-slate-900">
                  {(lead.possession || []).length ? lead.possession.map((p) => <Badge key={p} cls="bg-slate-100 text-slate-700 ring-slate-400/20 mr-1 mb-1">{p}</Badge>) : "—"}
                </div>
              </div>
              {allowsFollowUp(lead.status) && (
                <div className="sm:col-span-3">
                  <div className="text-xs text-slate-400 mb-1">Follow-up</div>
                  <div className={`text-sm font-medium ${overdue ? "text-red-700" : "text-slate-900"} flex items-center gap-2`}>
                    {lead.followUpAt ? (
                      <>
                        <Calendar className="w-4 h-4" />
                        {lead.followUpAt.toLocaleDateString("en-IN", { day: "2-digit", month: "long", year: "numeric" })}
                        <Clock className="w-4 h-4 ml-2" />
                        {lead.followUpAt.toLocaleTimeString("en-IN", { hour: "numeric", minute: "2-digit", hour12: true })}
                        {overdue && <Badge cls="bg-red-50 text-red-700 ring-red-600/20 ml-2">Overdue</Badge>}
                      </>
                    ) : <span className="text-slate-400">No follow-up scheduled</span>}
                  </div>
                </div>
              )}
              <div className="sm:col-span-3">
                <div className="text-xs text-slate-400 mb-1">Remark</div>
                <div className="text-sm text-slate-700 whitespace-pre-wrap">
                  {lead.remark || <span className="text-slate-300">—</span>}
                </div>
              </div>
              {lead.note && (
                <div className="sm:col-span-3">
                  <div className="text-xs text-slate-400 mb-1">Note</div>
                  <div className="text-sm text-slate-700 whitespace-pre-wrap">{lead.note}</div>
                </div>
              )}
            </div>
            <div className="flex flex-wrap gap-2 mt-5">
              <Btn variant="primary" onClick={() => window.open("https://wa.me/91" + (lead.whatsapp || lead.mobile), "_blank")}><MessageCircle className="w-4 h-4" />Send WhatsApp</Btn>
              <Btn variant="secondary" onClick={() => { window.location.href = "tel:+91" + lead.mobile; }}><Phone className="w-4 h-4" />Call</Btn>
              {allowsFollowUp(lead.status) && (
                <Btn variant="secondary" onClick={() => setEditing(true)}><Calendar className="w-4 h-4" />Schedule follow-up</Btn>
              )}
            </div>
          </Card>

          <Card className="p-5">
            <h3 className="text-sm font-semibold text-slate-900 mb-4">Activity timeline</h3>
            <div className="space-y-4">
              {timeline.map((t, i) => (
                <div key={i} className="flex gap-3">
                  <div className="flex flex-col items-center">
                    <div className="w-7 h-7 rounded-full bg-slate-100 grid place-items-center shrink-0">
                      {t.type === "status" && <CheckCircle2 className="w-3.5 h-3.5 text-slate-500" />}
                      {t.type === "note" && <Edit3 className="w-3.5 h-3.5 text-slate-500" />}
                      {t.type === "whatsapp" && <MessageCircle className="w-3.5 h-3.5 text-slate-500" />}
                      {t.type === "created" && <Plus className="w-3.5 h-3.5 text-slate-500" />}
                    </div>
                    {i < timeline.length - 1 && <div className="w-px flex-1 bg-slate-200 my-1" />}
                  </div>
                  <div className="pb-1">
                    <p className="text-sm text-slate-700">{t.text}</p>
                    <p className="text-xs text-slate-400 mt-0.5">{t.who} · {fmtDate(t.when)} {fmtTime(t.when)}</p>
                  </div>
                </div>
              ))}
            </div>
            <div className="mt-5 pt-5 border-t border-slate-100">
              <textarea
                value={note}
                onChange={(e) => setNote(e.target.value)}
                placeholder="Add a note…"
                rows={2}
                className="w-full text-sm rounded-lg ring-1 ring-inset ring-slate-300 px-3 py-2 resize-none focus:outline-none focus:ring-blue-500"
              />
              <div className="flex justify-end mt-2">
                <Btn variant="primary" size="sm" onClick={handleAddNote} disabled={notePosting || !note.trim()}>
                  {notePosting ? "Adding\u2026" : "Add note"}
                </Btn>
              </div>
            </div>
          </Card>
        </div>

        <div className="space-y-6">
          <Card className="p-5">
            <h3 className="text-sm font-semibold text-slate-900 mb-3">Status</h3>
            <select
              value={status}
              onChange={(e) => {
                setStatus(e.target.value);
                onUpdate?.({ ...lead, status: e.target.value, lastStatusChangeAt: new Date(), lastActivity: new Date() });
              }}
              className="w-full text-sm rounded-lg ring-1 ring-inset ring-slate-300 px-3 py-2 bg-white focus:outline-none focus:ring-blue-500"
            >
              {STATUSES.map((s) => <option key={s} value={s}>{STATUS_META[s]?.label || s}</option>)}
            </select>
          </Card>

          {role !== "sales" && (
            <Card className="p-5">
              <h3 className="text-sm font-semibold text-slate-900 mb-2">Assigned to</h3>
              {assigneeIds.length > 0 ? (
                <div className="flex flex-wrap gap-1.5">
                  {assigneeIds.map((id) => {
                    const sp = allSalesPeople.find((s) => (s.userId || s.id) === id);
                    return <Badge key={id} cls="bg-blue-50 text-blue-700 ring-blue-600/20">{sp?.name || "Unknown"}</Badge>;
                  })}
                </div>
              ) : (
                <Badge cls="bg-amber-50 text-amber-700 ring-amber-600/20">Unassigned</Badge>
              )}
              <p className="text-[10px] text-slate-400 mt-2">Use Transfer (above) or Edit lead for multi-assign.</p>
            </Card>
          )}

          <Card className="p-5">
            <h3 className="text-sm font-semibold text-slate-900 mb-3">Temperature</h3>
            <div className="flex gap-2">
              {["hot", "warm", "cold"].map((t) => {
                const meta = TEMPERATURE_META[t];
                const active = lead.temperature === t;
                return (
                  <button
                    key={t}
                    onClick={() => onUpdate?.({ ...lead, temperature: t, lastActivity: new Date() })}
                    className={`flex-1 text-xs font-medium py-1.5 rounded-lg ring-1 ring-inset ${active ? meta.cls + " ring-2" : "bg-white text-slate-600 ring-slate-300 hover:bg-slate-50"}`}
                  >
                    {meta.label}
                  </button>
                );
              })}
            </div>
          </Card>
        </div>
      </div>

      {editing && (
        <LeadForm
          lead={lead}
          role={role}
          statusVersion={statusVersion}
          onClose={() => setEditing(false)}
          onSave={async (patched) => {
            try {
              await onUpdate?.(patched);
              setEditing(false);
              toast("Lead updated");
            } catch (err) {
              toast(err?.message || "Failed to save");
            }
          }}
        />
      )}
    </div>
  );
}
